import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const API = 'https://groovie-business-api-production.up.railway.app/api/v1';

const VOICE_STYLES = [
  { value: 'professional', label: 'DJ Sarah', desc: 'Clear, corporate tone', icon: 'mic' },
  { value: 'friendly', label: 'DJ Kira', desc: 'Warm and welcoming', icon: 'smile' },
  { value: 'energetic', label: 'DJ Mike', desc: 'Upbeat and exciting', icon: 'zap' },
  { value: 'male_narrator', label: 'DJ Roger', desc: 'Deep, authoritative', icon: 'mic' },
  { value: 'calm', label: 'Calm', desc: 'Relaxed and soothing', icon: 'cloud' }
];

const ANNOUNCEMENT_TYPES = [
  { value: 'general', label: 'General', bg: '#eff6ff', color: '#2563eb' },
  { value: 'promotion', label: 'Promotion', bg: '#ecfdf5', color: '#059669' },
  { value: 'event', label: 'Event', bg: '#f5f3ff', color: '#7c3aed' },
  { value: 'emergency', label: 'Emergency', bg: '#fef2f2', color: '#dc2626' }
];

const FREQUENCY_OPTIONS = [
  { value: 'once', label: 'Play Once', desc: 'Single play, then done' },
  { value: 'hourly', label: 'Hourly', desc: 'Repeats every hour' },
  { value: 'daily', label: 'Daily', desc: 'Plays once per day' },
  { value: 'instant', label: 'Play Now', desc: 'Immediately interrupt and play' }
];

const DEFAULT_FORM = {
  title: '', content: '', channel_id: '',
  announcement_type: 'general', priority: 5,
  play_frequency: 'hourly', voice_style: 'professional',
  delivery: 'scheduled',
  start_date: '', end_date: ''
};

// Three business-facing delivery choices (no mechanics exposed). "immediate" is owner/admin only.
const DELIVERY_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled', desc: 'Plays according to its schedule' },
  { value: 'next_break', label: 'Next break', desc: 'Plays at the next available break' },
  { value: 'immediate', label: 'Immediate', desc: 'Interrupts and plays now', restricted: true }
];

const Svg = ({ type, size = 16 }) => {
  const icons = {
    play: <polygon points="5,3 19,12 5,21" fill="currentColor" />,
    stop: <rect x="4" y="4" width="16" height="16" rx="2" fill="currentColor" />,
    plus: <><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>,
    edit: <><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></>,
    trash: <><polyline points="3,6 5,6 21,6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></>,
    megaphone: <><path d="m3 11 18-5v12L3 13v-2z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></>,
    radio: <><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"/><circle cx="12" cy="12" r="2"/><path d="M19.1 4.9C23 8.8 23 15.1 19.1 19"/></>,
    mic: <><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/></>,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
    power: <><path d="M18.36 6.64a9 9 0 1 1-12.73 0"/><line x1="12" y1="2" x2="12" y2="12"/></>,
    volume: <><polygon points="11,5 6,9 2,9 2,15 6,15 11,19"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{icons[type]}</svg>
  );
};

const inputStyle = {
  width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 10,
  padding: '11px 14px', fontSize: 14, outline: 'none',
  fontFamily: "'Outfit', sans-serif", boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

export default function Announcements() {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ ...DEFAULT_FORM });
  const [editingId, setEditingId] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [previewingVoice, setPreviewingVoice] = useState(false);
  const audioRef = useRef(new Audio());
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };
  const jsonHeaders = { ...headers, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    fetchAnnouncements(); fetchChannels();
    return () => { audioRef.current.pause(); };
  }, [token, navigate]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/announcements`, { headers });
      if (res.status === 401) { navigate('/login'); return; }
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setAnnouncements(data.announcements || []);
    } catch (err) { console.error(err); setAnnouncements([]); }
    finally { setLoading(false); }
  };

  const fetchChannels = async () => {
    try {
      const res = await fetch(`${API}/channels`, { headers });
      if (res.ok) { const data = await res.json(); setChannels(data.channels || []); }
    } catch (err) { console.error(err); }
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...formData };
      if (!payload.channel_id) delete payload.channel_id;
      else payload.channel_id = parseInt(payload.channel_id);
      if (!payload.start_date) delete payload.start_date;
      if (!payload.end_date) delete payload.end_date;
      const res = await fetch(`${API}/announcements`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(payload) });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Failed'); }
      const result = await res.json();
      await fetchAnnouncements();
      setShowCreateModal(false); setFormData({ ...DEFAULT_FORM });
      alert(result.tts?.status === 'generated' ? 'Announcement created and audio generated!' : 'Announcement created. Audio generation pending.');
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleEdit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...formData };
      if (!payload.channel_id) payload.channel_id = null;
      else payload.channel_id = parseInt(payload.channel_id);
      if (!payload.start_date) delete payload.start_date;
      if (!payload.end_date) delete payload.end_date;
      const res = await fetch(`${API}/announcements/${editingId}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error('Failed');
      await fetchAnnouncements(); setShowEditModal(false); setEditingId(null);
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete announcement "${title}"?`)) return;
    try {
      const res = await fetch(`${API}/announcements/${id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error('Failed');
      await fetchAnnouncements();
    } catch (err) { alert('Failed to delete'); }
  };

  const toggleActive = async (id) => {
    try {
      const res = await fetch(`${API}/announcements/${id}/toggle-active`, { method: 'PUT', headers });
      if (!res.ok) throw new Error('Failed');
      await fetchAnnouncements();
    } catch (err) { alert('Failed to toggle'); }
  };

  const openEditModal = (ann) => {
    setFormData({
      title: ann.title || '', content: ann.content || '',
      channel_id: ann.channel_id ? String(ann.channel_id) : '',
      announcement_type: ann.announcement_type || 'general',
      priority: ann.priority || 5, play_frequency: ann.play_frequency || 'hourly',
      voice_style: ann.voice_style || 'professional',
      delivery: ann.delivery || 'scheduled',
      start_date: ann.start_date ? ann.start_date.slice(0, 16) : '',
      end_date: ann.end_date ? ann.end_date.slice(0, 16) : ''
    });
    setEditingId(ann.id); setShowEditModal(true);
  };

  const togglePreview = (ann) => {
    if (playingId === ann.id) {
      audioRef.current.pause(); audioRef.current.src = ''; setPlayingId(null);
    } else if (ann.audio_url) {
      const fullUrl = ann.audio_url.startsWith('http') ? ann.audio_url : `https://groovie-business-api-production.up.railway.app${ann.audio_url}`;
      audioRef.current.src = fullUrl;
      audioRef.current.play().catch(() => alert('Audio not yet available.'));
      setPlayingId(ann.id);
      audioRef.current.onended = () => setPlayingId(null);
    } else { alert('No audio generated yet.'); }
  };

  const previewVoice = async () => {
    if (!formData.content.trim()) { alert('Enter announcement text first.'); return; }
    setPreviewingVoice(true);
    try {
      const res = await fetch(`${API}/announcements/preview-voice?text=${encodeURIComponent(formData.content.slice(0, 200))}&voice_style=${formData.voice_style}`, { method: 'POST', headers });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Preview failed'); }
      const data = await res.json();
      if (data.audio_data) { audioRef.current.src = data.audio_data; audioRef.current.play().catch(() => alert('Could not play.')); }
    } catch (err) { alert('Voice preview unavailable: ' + err.message); }
    finally { setPreviewingVoice(false); }
  };

  const filtered = announcements.filter(ann => {
    if (filter === 'active') return ann.is_active;
    if (filter === 'inactive') return !ann.is_active;
    if (filter === 'once') return ann.play_frequency === 'once' || ann.play_frequency === 'instant';
    if (filter === 'recurring') return ann.play_frequency === 'hourly' || ann.play_frequency === 'daily';
    return true;
  });

  const getTypeInfo = (val) => ANNOUNCEMENT_TYPES.find(t => t.value === val) || ANNOUNCEMENT_TYPES[0];
  const getVoiceInfo = (val) => VOICE_STYLES.find(v => v.value === val) || VOICE_STYLES[0];

  const renderForm = (onSubmit, isEdit) => (
    <form onSubmit={onSubmit}>
      <div style={{ marginBottom: 18 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Title *</label>
        <input type="text" required value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="e.g. Weekend Sale Announcement" style={inputStyle}
          onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'} />
      </div>
      <div style={{ marginBottom: 18 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Announcement Script *</label>
        <textarea required rows={4} value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          placeholder="This is what the AI voice will say on air..."
          style={{ ...inputStyle, resize: 'vertical' }}
          onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'} />
        <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{formData.content.length} characters &middot; ~{Math.ceil(formData.content.length / 15)}s audio</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Type</label>
          <select value={formData.announcement_type}
            onChange={(e) => setFormData({ ...formData, announcement_type: e.target.value })}
            style={{ ...inputStyle, background: '#fff' }}>
            {ANNOUNCEMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Priority</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input type="range" min="1" max="10" value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
              style={{ flex: 1, accentColor: '#7c3aed' }} />
            <span style={{ fontSize: 16, fontWeight: 700, color: '#7c3aed', width: 24, textAlign: 'center' }}>{formData.priority}</span>
          </div>
        </div>
      </div>
      <div style={{ marginBottom: 18 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Delivery</label>
        {(() => {
          const role = (JSON.parse(localStorage.getItem('user') || '{}').role) || 'viewer';
          const canImmediate = role === 'owner' || role === 'admin';
          return (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {DELIVERY_OPTIONS.map(d => {
                const locked = d.restricted && !canImmediate;
                const active = formData.delivery === d.value;
                return (
                  <button key={d.value} type="button" disabled={locked}
                    onClick={() => !locked && setFormData({ ...formData, delivery: d.value })}
                    title={locked ? 'Immediate delivery is available to owner and admin roles' : d.desc}
                    style={{
                      padding: '10px 8px', borderRadius: 10, textAlign: 'left', cursor: locked ? 'not-allowed' : 'pointer',
                      border: active ? '2px solid #7c3aed' : '1px solid #e5e7eb',
                      background: active ? '#f5f3ff' : '#fff', opacity: locked ? 0.5 : 1
                    }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: active ? '#7c3aed' : '#374151' }}>{d.label}{locked ? ' \u{1F512}' : ''}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{d.desc}</div>
                  </button>
                );
              })}
            </div>
          );
        })()}
      </div>
      <div style={{ marginBottom: 18 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Play Frequency</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {FREQUENCY_OPTIONS.map(f => (
            <button key={f.value} type="button" onClick={() => setFormData({ ...formData, play_frequency: f.value })}
              style={{
                padding: '12px 14px', borderRadius: 10, textAlign: 'left',
                border: formData.play_frequency === f.value ? '2px solid #7c3aed' : '1.5px solid #e5e7eb',
                background: formData.play_frequency === f.value ? '#f5f3ff' : '#fff',
                cursor: 'pointer', transition: 'all 0.15s',
              }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{f.label}</div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{f.desc}</div>
            </button>
          ))}
        </div>
      </div>
      <div style={{ marginBottom: 18 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>AI Voice</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {VOICE_STYLES.map(v => (
            <button key={v.value} type="button" onClick={() => setFormData({ ...formData, voice_style: v.value })}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 14px', borderRadius: 10, textAlign: 'left',
                border: formData.voice_style === v.value ? '2px solid #7c3aed' : '1.5px solid #e5e7eb',
                background: formData.voice_style === v.value ? '#f5f3ff' : '#fff',
                cursor: 'pointer', transition: 'all 0.15s',
              }}>
              <div style={{ color: formData.voice_style === v.value ? '#7c3aed' : '#9ca3af' }}>
                <Svg type="mic" size={18} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{v.label}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{v.desc}</div>
              </div>
            </button>
          ))}
        </div>
        <button type="button" onClick={previewVoice} disabled={previewingVoice || !formData.content.trim()}
          style={{
            width: '100%', marginTop: 10, padding: '10px 16px', borderRadius: 10,
            border: '1.5px solid #dbeafe', background: '#eff6ff', color: '#2563eb',
            fontSize: 13, fontWeight: 600, cursor: previewingVoice ? 'not-allowed' : 'pointer',
            opacity: !formData.content.trim() ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
          <Svg type="volume" size={16} /> {previewingVoice ? 'Generating...' : 'Preview This Voice'}
        </button>
      </div>
      <div style={{ marginBottom: 18 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Assign to Channel</label>
        <select value={formData.channel_id}
          onChange={(e) => setFormData({ ...formData, channel_id: e.target.value })}
          style={{ ...inputStyle, background: '#fff' }}>
          <option value="">All Channels</option>
          {channels.map(ch => <option key={ch.id} value={ch.id}>{ch.name}</option>)}
        </select>
        <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Leave blank to broadcast on all channels</p>
      </div>
      {formData.play_frequency !== 'once' && formData.play_frequency !== 'instant' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Start Date</label>
            <input type="datetime-local" value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>End Date</label>
            <input type="datetime-local" value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} style={inputStyle} />
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 12, borderTop: '1px solid #f3f4f6', paddingTop: 20 }}>
        <button type="button" onClick={() => { setShowCreateModal(false); setShowEditModal(false); }}
          style={{
            flex: 1, padding: '12px 20px', borderRadius: 10,
            border: '1.5px solid #e5e7eb', background: '#fff',
            color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: "'Outfit', sans-serif",
          }}>Cancel</button>
        <button type="submit" disabled={saving}
          style={{
            flex: 1, padding: '12px 20px', borderRadius: 10,
            border: 'none', background: saving ? '#c4b5fd' : 'linear-gradient(135deg, #7c3aed, #a855f7)',
            color: '#fff', fontSize: 14, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
            fontFamily: "'Outfit', sans-serif", boxShadow: '0 4px 12px rgba(124,58,237,0.3)',
          }}>{saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Announcement')}</button>
      </div>
    </form>
  );

  if (loading) {
    return (
      <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', border: '3px solid #e9d5ff', borderTopColor: '#7c3aed', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1200, margin: '0 auto', fontFamily: "'Outfit', 'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.5px' }}>Announcements</h1>
          <p style={{ fontSize: 14, color: '#9ca3af', marginTop: 4 }}>{announcements.length} announcement{announcements.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => { setFormData({ ...DEFAULT_FORM }); setShowCreateModal(true); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
            color: '#fff', border: 'none', borderRadius: 12,
            padding: '12px 24px', fontSize: 14, fontWeight: 600,
            cursor: 'pointer', fontFamily: "'Outfit', sans-serif",
            boxShadow: '0 4px 12px rgba(124,58,237,0.3)',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(124,58,237,0.4)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(124,58,237,0.3)'; }}
        >
          <Svg type="plus" size={18} /> New Announcement
        </button>
      </div>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { key: 'all', label: 'All' },
          { key: 'active', label: 'Active' },
          { key: 'inactive', label: 'Inactive' },
          { key: 'once', label: 'One-Time' },
          { key: 'recurring', label: 'Recurring' }
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            style={{
              padding: '8px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600,
              border: 'none', cursor: 'pointer', transition: 'all 0.15s',
              background: filter === f.key ? '#7c3aed' : '#f3f4f6',
              color: filter === f.key ? '#fff' : '#6b7280',
            }}>{f.label}</button>
        ))}
      </div>

      {/* Empty State */}
      {filtered.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '64px 24px',
          background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb',
        }}>
          <div style={{ color: '#9ca3af', marginBottom: 16 }}><Svg type="megaphone" size={48} /></div>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: '#374151', margin: '0 0 8px' }}>
            {filter === 'all' ? 'No announcements yet' : `No ${filter} announcements`}
          </h3>
          <p style={{ fontSize: 14, color: '#9ca3af', margin: '0 0 24px' }}>Create announcements that play on your radio channels</p>
          {filter === 'all' && (
            <button onClick={() => { setFormData({ ...DEFAULT_FORM }); setShowCreateModal(true); }}
              style={{
                background: 'linear-gradient(135deg, #7c3aed, #a855f7)', color: '#fff',
                border: 'none', borderRadius: 10, padding: '12px 28px',
                fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}>Create Your First Announcement</button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(ann => {
            const typeInfo = getTypeInfo(ann.announcement_type);
            const voiceInfo = getVoiceInfo(ann.voice_style);
            const channelName = channels.find(c => c.id === ann.channel_id)?.name || 'All Channels';
            const isOnce = ann.play_frequency === 'once' || ann.play_frequency === 'instant';

            return (
              <div key={ann.id} style={{
                background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb',
                overflow: 'hidden', transition: 'box-shadow 0.15s',
                opacity: ann.is_active ? 1 : 0.6,
              }}
              onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
              >
                <div style={{ padding: '20px 24px' }}>
                  {/* Top row */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                        <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: 0 }}>{ann.title}</h3>
                        <span style={{
                          padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                          background: typeInfo.bg, color: typeInfo.color,
                        }}>{typeInfo.label}</span>
                        <span style={{
                          padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                          background: isOnce ? '#fffbeb' : '#eef2ff',
                          color: isOnce ? '#d97706' : '#4f46e5',
                        }}>{isOnce ? 'One-Time' : ann.play_frequency}</span>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                          background: ann.is_active ? '#ecfdf5' : '#f3f4f6',
                          color: ann.is_active ? '#059669' : '#6b7280',
                        }}>
                          <span style={{
                            width: 6, height: 6, borderRadius: '50%',
                            background: ann.is_active ? '#059669' : '#9ca3af',
                          }} />
                          {ann.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p style={{ fontSize: 13, color: '#6b7280', margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{ann.content}</p>
                    </div>
                    <div style={{ textAlign: 'center', flexShrink: 0 }}>
                      <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Priority</div>
                      <div style={{
                        fontSize: 20, fontWeight: 800, marginTop: 2,
                        color: ann.priority >= 8 ? '#dc2626' : ann.priority >= 5 ? '#d97706' : '#6b7280',
                      }}>{ann.priority}</div>
                    </div>
                  </div>

                  {/* Info row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 12, color: '#9ca3af', marginBottom: 14, flexWrap: 'wrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Svg type="mic" size={12} /> {voiceInfo.label}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Svg type="radio" size={12} /> {channelName}</span>
                    {ann.start_date && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Svg type="calendar" size={12} /> {new Date(ann.start_date).toLocaleDateString()}</span>}
                    {ann.end_date && <span>&rarr; {new Date(ann.end_date).toLocaleDateString()}</span>}
                    <span style={{ fontSize: 11 }}>Created {new Date(ann.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Actions */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '12px 24px', borderTop: '1px solid #f3f4f6', background: '#fafafa',
                }}>
                  <button onClick={() => togglePreview(ann)} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                    border: 'none', cursor: 'pointer',
                    background: playingId === ann.id ? '#fef2f2' : '#eff6ff',
                    color: playingId === ann.id ? '#dc2626' : '#2563eb',
                  }}>
                    <Svg type={playingId === ann.id ? 'stop' : 'play'} size={12} />
                    {playingId === ann.id ? 'Stop' : 'Preview'}
                  </button>
                  <button onClick={() => toggleActive(ann.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                    border: 'none', cursor: 'pointer',
                    background: ann.is_active ? '#fffbeb' : '#ecfdf5',
                    color: ann.is_active ? '#d97706' : '#059669',
                  }}>
                    <Svg type="power" size={12} />
                    {ann.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button onClick={() => openEditModal(ann)} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                    border: 'none', cursor: 'pointer', background: '#f3f4f6', color: '#4b5563',
                  }}>
                    <Svg type="edit" size={12} /> Edit
                  </button>
                  <div style={{ flex: 1 }} />
                  <button onClick={() => handleDelete(ann.id, ann.title)} style={{
                    padding: 7, borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: 'transparent', color: '#d1d5db',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#d1d5db'}
                  ><Svg type="trash" size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* CREATE MODAL */}
      {showCreateModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 50, padding: 16, backdropFilter: 'blur(4px)',
        }}>
          <div style={{
            background: '#fff', borderRadius: 20, width: '100%', maxWidth: 520,
            maxHeight: '90vh', overflowY: 'auto', padding: 32,
            boxShadow: '0 25px 60px rgba(0,0,0,0.15)',
          }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 24px' }}>New Announcement</h2>
            {renderForm(handleCreate, false)}
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {showEditModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 50, padding: 16, backdropFilter: 'blur(4px)',
        }}>
          <div style={{
            background: '#fff', borderRadius: 20, width: '100%', maxWidth: 520,
            maxHeight: '90vh', overflowY: 'auto', padding: 32,
            boxShadow: '0 25px 60px rgba(0,0,0,0.15)',
          }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 24px' }}>Edit Announcement</h2>
            {renderForm(handleEdit, true)}
          </div>
        </div>
      )}
    </div>
  );
}